import React, { useEffect, useRef, useState } from 'react';
import { API_ENDPOINTS } from '../config/api';

export default function TrackedNotice({ item, type = 'announcement', children, className = '', onReceipt }) {
  const element = useRef(null);
  const pending = useRef(false);
  const lastAction = useRef('read');
  const callback = useRef(onReceipt);
  callback.current = onReceipt;
  const [receipt, setReceipt] = useState(item);
  const current = useRef(receipt);
  current.current = receipt;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const confirm = useRef(null);
  confirm.current = async action => {
    if (pending.current) return;
    pending.current = true;
    lastAction.current = action;
    setBusy(true);
    setError('');
    try {
      const response = await fetch(`${API_ENDPOINTS.BASE_URL}/api/notifications/${type}/${encodeURIComponent(item.id)}/${action}`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: '{}'
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Unable to save confirmation. Please retry.');
      current.current = { ...current.current, ...data.receipt };
      setReceipt(current.current);
      callback.current?.(data.receipt);
    } catch (error) { setError(error.message); }
    finally { pending.current = false; setBusy(false); }
  };

  useEffect(() => {
    let timer;
    let visible = false;
    const cancel = () => clearTimeout(timer);
    const schedule = () => {
      cancel();
      if (visible && !document.hidden && !current.current.isRead) {
        timer = setTimeout(() => confirm.current('read'), 1000);
      }
    };
    const check = () => {
      const rect = element.current?.getBoundingClientRect();
      visible = Boolean(rect && rect.height > 0 && Math.min(rect.bottom, window.innerHeight) - Math.max(rect.top, 0) >= Math.min(120, rect.height / 2));
      schedule();
    };
    const observer = typeof IntersectionObserver === 'function' ? new IntersectionObserver(entries => {
      const entry = entries[0];
      visible = entry.isIntersecting && entry.intersectionRect.height >= Math.min(120, entry.boundingClientRect.height / 2);
      schedule();
    }, { threshold: [0, 0.1, 0.25, 0.5, 1] }) : null;
    if (observer) observer.observe(element.current);
    else { check(); window.addEventListener('scroll', check); window.addEventListener('resize', check); }
    document.addEventListener('visibilitychange', schedule);
    return () => {
      cancel(); observer?.disconnect();
      document.removeEventListener('visibilitychange', schedule);
      window.removeEventListener('scroll', check); window.removeEventListener('resize', check);
    };
  }, [item.id, type]);

  return <article ref={element} className={className} aria-label={item.title}>
    {children}
    <div className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-200 px-4 py-3 text-sm">
      <span className={receipt.isRead ? 'text-emerald-700' : 'text-slate-500'}>
        {receipt.isRead ? 'Viewed' : 'Not viewed yet'}
        {receipt.readAt && `: ${new Date(receipt.readAt).toLocaleString('en-NG', { timeZone: 'Africa/Lagos' })}`}
      </span>
      {type === 'announcement' && <button type="button" disabled={busy || Boolean(receipt.acknowledgedAt)}
        onClick={() => confirm.current('acknowledge')}
        className="bg-emerald-700 text-white px-4 py-2 rounded disabled:opacity-60">
        {receipt.acknowledgedAt ? 'Acknowledged' : busy ? 'Saving...' : 'Acknowledge'}
      </button>}
      {receipt.acknowledgedAt && <span className="text-emerald-700">Acknowledged: {new Date(receipt.acknowledgedAt).toLocaleString('en-NG', { timeZone: 'Africa/Lagos' })}</span>}
      {error && <p role="alert" className="w-full text-red-700">{error} <button type="button" className="underline" disabled={busy}
        onClick={() => confirm.current(lastAction.current)}>Retry</button></p>}
    </div>
  </article>;
}
